import { MessageContext } from '@/contexts/MessageContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
  ChainlitContext,
  IFeedback,
  IMessageElement,
  IStep,
  messagesState,
  sideViewState,
  updateMessageById,
  useChatData,
  useChatInteract,
  useChatMessages,
  useConfig
} from '@chainlit/react-client';

import { Messages } from '@/components/chat/Messages';
import { AssistantThinking } from '@/components/chat/Messages/AssistantThinking';
import { useTranslation } from 'components/i18n/Translator';
import { snowAttachmentFlowState } from 'state/chat';

interface Props {
  navigate?: (to: string) => void;
}

const MessagesContainer = ({ navigate }: Props) => {
  const apiClient = useContext(ChainlitContext);
  const { config } = useConfig();
  const { elements, askUser, loading, actions } = useChatData();
  const { messages } = useChatMessages();
  const { uploadFile: _uploadFile } = useChatInteract();
  const setMessages = useSetRecoilState(messagesState);
  const setSideView = useSetRecoilState(sideViewState);
  const setSnowAttachmentFlow = useSetRecoilState(snowAttachmentFlowState);

  const parseStepMetadata = useCallback((metadata: any) => {
    if (!metadata) {
      return null;
    }

    // If it's already an object, use it directly
    if (typeof metadata === 'object' && metadata !== null) {
      return metadata;
    }

    // If it's a string, try to parse it as JSON
    if (typeof metadata === 'string') {
      try {
        const parsed = JSON.parse(metadata);
        return parsed;
      } catch (error) {
        return null;
      }
    }

    return null;
  }, []);

  const { t } = useTranslation();

  const [frontendStreamingIds, setFrontendStreamingIds] = useState<Set<string>>(
    () => new Set()
  );

  const uploadFile = useCallback(
    (file: File, onProgress: (progress: number) => void) => {
      return _uploadFile(file, onProgress);
    },
    [_uploadFile]
  );

  useEffect(() => {
    if (!messages || !messages.length) {
      setSnowAttachmentFlow(false);
      return;
    }

    // Helper function to recursively search for ui_hints in message and nested steps
    const findMessageWithHints = (step: any): { step: any, parsed: any } | null => {
      const parsed = parseStepMetadata(step.metadata);
      const hasHints = Boolean(parsed?.ui_hints);
      
      if (hasHints) {
        return { step, parsed };
      }
      
      // Recursively check nested steps
      if (step.steps && Array.isArray(step.steps)) {
        for (const nestedStep of step.steps) {
          const found = findMessageWithHints(nestedStep);
          if (found) {
            return found;
          }
        }
      }
      
      return null;
    };

    const reversedMessages = [...messages].reverse();
    const result = reversedMessages.map(msg => findMessageWithHints(msg)).find(r => r !== null);
    const lastWithHints = result?.step;

    if (!lastWithHints) {
      setSnowAttachmentFlow(false);
      return;
    }

    const metadataRecord = parseStepMetadata(lastWithHints.metadata);
    if (!metadataRecord) {
      setSnowAttachmentFlow(false);
      return;
    }

    const hintsRaw = metadataRecord['ui_hints'];
    const hints =
      hintsRaw && typeof hintsRaw === 'object'
        ? (hintsRaw as Record<string, unknown>)
        : undefined;

    if (!hints) {
      setSnowAttachmentFlow(false);
      return;
    }

    const after = hints['snow_attachment_flow_after'];
    const current = hints['snow_attachment_flow'];

    if (typeof after === 'boolean') {
      setSnowAttachmentFlow(after);
      return;
    }

    if (typeof current === 'boolean') {
      setSnowAttachmentFlow(current);
      return;
    }

    setSnowAttachmentFlow(false);
  }, [messages, parseStepMetadata, setSnowAttachmentFlow]);

  const onFeedbackUpdated = useCallback(
    async (message: IStep, onSuccess: () => void, feedback: IFeedback) => {
      toast.promise(apiClient.setFeedback(feedback), {
        loading: t('chat.messages.feedback.status.updating'),
        success: (res) => {
          setMessages((prev) =>
            updateMessageById(prev, message.id, {
              ...message,
              feedback: {
                ...feedback,
                id: res.feedbackId
              }
            })
          );
          onSuccess();
          return t('chat.messages.feedback.status.updated');
        },
        error: (err) => {
          return <span>{err.message}</span>;
        }
      });
    },
    []
  );

  const onFeedbackDeleted = useCallback(
    async (message: IStep, onSuccess: () => void, feedbackId: string) => {
      toast.promise(apiClient.deleteFeedback(feedbackId), {
        loading: t('chat.messages.feedback.status.updating'),
        success: () => {
          setMessages((prev) =>
            updateMessageById(prev, message.id, {
              ...message,
              feedback: undefined
            })
          );
          onSuccess();
          return t('chat.messages.feedback.status.updated');
        },
        error: (err) => {
          return <span>{err.message}</span>;
        }
      });
    },
    []
  );

  const handleFrontendStreamingChange = useCallback(
    (messageId: string, active: boolean) => {
      setFrontendStreamingIds((prev) => {
        const next = new Set(prev);
        if (active) {
          next.add(messageId);
        } else {
          next.delete(messageId);
        }
        return next;
      });
    },
    []
  );

  const onElementRefClick = useCallback(
    (element: IMessageElement) => {
      if (
        element.display === 'side' ||
        (element.display === 'page' && !navigate)
      ) {
        setSideView({ title: element.name, elements: [element] });
        return;
      }

      let path = `/element/${element.id}`;

      if (element.threadId) {
        path += `?thread=${element.threadId}`;
      }

      return navigate?.(element.display === 'page' ? path : '#');
    },
    [setSideView, navigate]
  );

  const onError = useCallback((error: string) => toast.error(error), [toast]);

  const enableFeedback = !!config?.dataPersistence;

  // Memoize the context object since it's created on each render.
  // This prevents unnecessary re-renders of children components when no props have changed.
  const memoizedContext = useMemo(() => {
    return {
      uploadFile,
      askUser,
      allowHtml: config?.features?.unsafe_allow_html,
      latex: config?.features?.latex,
      editable: !!config?.features.edit_message,
      loading,
      showFeedbackButtons: enableFeedback,
      uiName: config?.ui?.name || '',
      cot: config?.ui?.cot || 'hidden',
      onElementRefClick,
      onError,
      onFeedbackUpdated,
      onFeedbackDeleted,
      onFrontendStreamingChange: handleFrontendStreamingChange
    };
  }, [
    uploadFile,
    askUser,
    enableFeedback,
    loading,
    config?.ui?.name,
    config?.ui?.cot,
    config?.features?.unsafe_allow_html,
    config?.features?.latex,
    config?.features?.edit_message,
    onElementRefClick,
    onError,
    onFeedbackUpdated,
    onFeedbackDeleted,
    handleFrontendStreamingChange
  ]);

  const activeFrontendStreaming = frontendStreamingIds.size > 0;

  const showThinkingIndicator = useMemo(() => {
    if (!loading) {
      return false;
    }

    if (activeFrontendStreaming) {
      return false;
    }

    const dialogSteps = [...messages].reverse().filter((step) =>
      step.type === 'assistant_message' || step.type === 'user_message'
    );

    if (!dialogSteps.length) {
      return false;
    }

    const latestDialogStep = dialogSteps[0];

    if (latestDialogStep.type === 'assistant_message') {
      return false;
    }

    return true;
  }, [loading, messages, activeFrontendStreaming]);

  return (
    <MessageContext.Provider value={memoizedContext}>
      <>
        <Messages
          indent={0}
          isRunning={loading}
          messages={messages}
          elements={elements}
          actions={actions}
        />
        {showThinkingIndicator ? (
          <AssistantThinking assistantName={config?.ui?.name || 'Assistant'} />
        ) : null}
      </>
    </MessageContext.Provider>
  );
};

export default MessagesContainer;
